// src/components/resources/tools/generators/AdMockup/StoryAd.tsx
'use client'

import { PhotoIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { CheckBadgeIcon as CheckBadgeSolidIcon } from '@heroicons/react/24/solid'
import { Avatar } from './Avatar'
import type { AdState } from './types'

/**
 * Full-screen Stories / Reels ad (9:16). Real Meta Stories ads render the
 * creative full-bleed with: segmented progress bars at the very top, a header
 * row (avatar + name + "Sponsored"), and a swipe-up CTA chip pinned to the
 * bottom. Uses the padding-bottom ratio technique (16/9) so the box reserves
 * height for its absolutely-positioned children on export (see CreativeSlot).
 */
export function StoryAd({ state, creativeImage, avatarImage }: { state: AdState; creativeImage: string | null; avatarImage: string | null }): JSX.Element {
  return (
    <div className="relative w-full overflow-hidden rounded-[28px] bg-black font-sans text-white" style={{ paddingBottom: `${(16 / 9) * 100}%` }}>
      {/* background creative */}
      {creativeImage ? (
        // eslint-disable-next-line @next/next/no-img-element -- user-uploaded data URL; next/image can't optimize data URIs and html2canvas needs the raw <img>
        <img src={creativeImage} alt={`${state.pageName} story creative`} className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-indigo-600 via-fuchsia-600 to-rose-500 text-white/80">
          <PhotoIcon className="h-10 w-10" />
          <span className="text-xs">Upload your vertical creative (9:16)</span>
        </div>
      )}

      {/* top scrim + progress segments */}
      <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/55 to-transparent" />
      <div className="absolute inset-x-0 top-2 flex gap-1 px-3">
        {[0, 1, 2].map(i => (
          <span key={i} className="h-0.5 flex-1 rounded-full bg-white/40">
            {i === 0 && <span className="block h-full w-2/3 rounded-full bg-white" />}
          </span>
        ))}
      </div>

      {/* header */}
      <div className="absolute inset-x-0 top-5 flex items-center gap-2 px-3 pt-1">
        <Avatar name={state.pageName} image={avatarImage} size={28} />
        <div className="flex min-w-0 items-center gap-1">
          <span className="truncate text-[13px] font-semibold drop-shadow">{state.pageName}</span>
          {state.verified && <CheckBadgeSolidIcon className="h-3.5 w-3.5 flex-shrink-0 text-white" aria-label="Verified" />}
          <span className="ml-1 text-[11px] font-medium text-white/80">Sponsored</span>
        </div>
        <span className="ml-auto text-lg leading-none text-white/90" aria-hidden>×</span>
      </div>

      {/* bottom scrim + caption + CTA chip */}
      <div className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-3 bg-gradient-to-t from-black/70 to-transparent px-5 pb-6 pt-16">
        {state.primaryText.trim() && (
          <p className="line-clamp-3 text-center text-sm font-medium leading-snug drop-shadow">{state.primaryText}</p>
        )}
        <span className="flex items-center gap-1 rounded-full bg-white px-6 py-2 text-sm font-semibold text-black shadow-lg">
          <ChevronRightIcon className="h-4 w-4 -rotate-90" aria-hidden />
          {state.cta}
        </span>
      </div>
    </div>
  )
}
